import { ApolloClient, ApolloError, ApolloQueryResult } from "@apollo/client";
import { IWizardExecuteAllModel } from "../../../components/Wizard";
import { IAdvancedEmailWizardContext } from "../../../context";
import { WizardTypeEnum } from "../../../enums/WizardTypeEnum";
import { IParticipantEmailDataModel } from "../../../containers/EmailWizard/models/ParticipantEmail";
import { ISendEmailDataModel } from "../../../containers/EmailWizard/models/SendEmail";
import { SendEmailExecuteModel } from "../../../containers/EmailWizard/models/SendEmail/SendEmailExecuteModel";
import { ITemplateDataModel } from "../../../containers/TemplateWizard/models/Template";
import { IDynamicTextValidationItem, IValidatedDynamicTextResult, IValidateDynamicTextResponse, validateDynamicTextQuery } from "../../../containers/Steps/Details/graphQL/validateDynamicText";
import { ExecuteModel } from "../../../containers/Steps/ExecuteModel";
import { IEmailPreview, IEmailPreviewHtml, previewEmailQuery } from "../../../containers/Steps/Preview/graphQL/emailPreview";

export class ScribanValidatorService {

    private apolloClient: ApolloClient<object>;
    private advancedEmailWizardContext: IAdvancedEmailWizardContext;

    constructor(apolloClient: ApolloClient<object>, advancedEmailWizardContext: IAdvancedEmailWizardContext) {
        this.apolloClient = apolloClient;
        this.advancedEmailWizardContext = advancedEmailWizardContext;
    }

    public async ValidateEmailContent(props: ExecuteModel<ITemplateDataModel | ISendEmailDataModel | IParticipantEmailDataModel>): Promise<IEmailPreview> {
        return new Promise<IEmailPreview>(async (resolve, reject) => {
            const ids = props.ids && props.ids[0];
            await this.validateBodyAndSubject(ids!, props)
                .then((response) => {
                    resolve(response);
                }).catch((error: ApolloError) => {
                    reject(error);
                });
        });
    }

    public async ValidateEmailDetails(values: IWizardExecuteAllModel): Promise<IValidatedDynamicTextResult[]> {
        return new Promise<IValidatedDynamicTextResult[]>(async (resolve, reject) => {

            // If we don't have the flag enabled or if we are in the Template Wizard, then don't call the endpoint
            if (!this.advancedEmailWizardContext.FeatureFlags?.enableGraphQLDynamicTextValidationEndpoint ||
                this.advancedEmailWizardContext.WizardType === WizardTypeEnum.Template) {
                resolve([]);
            }

            const id = values.ids && values.ids[0];
            const email = values as SendEmailExecuteModel<ISendEmailDataModel | IParticipantEmailDataModel>;

            let participantID: number | undefined;

            if (this.advancedEmailWizardContext.WizardType === WizardTypeEnum.Participant) {
                const participantEmail = values as SendEmailExecuteModel<IParticipantEmailDataModel>;
                participantID = participantEmail.formDataModel.ParticipantEntryIDs[0];
            }

            // Build up the dictionary
            const dictionaryToValidate: IDynamicTextValidationItem[] = [
                {
                    key: nameof.full<SendEmailExecuteModel<ISendEmailDataModel>>((m) => m.formDataModel.EmailSubject),
                    value: email.formDataModel.EmailSubject
                },
                {
                    key: nameof.full<SendEmailExecuteModel<ISendEmailDataModel>>((m) => m.formDataModel.EmailToItems),
                    value: email.formDataModel.EmailToItems.map((x) => x.value).join(";")
                },
                {
                    key: nameof.full<SendEmailExecuteModel<ISendEmailDataModel>>((m) => m.formDataModel.EmailCCAddressItems),
                    value: email.formDataModel.EmailCCAddressItems.map((x) => x.value).join(";")
                },
                {
                    key: nameof.full<SendEmailExecuteModel<ISendEmailDataModel>>((m) => m.formDataModel.EmailBCCAddressItems),
                    value: email.formDataModel.EmailBCCAddressItems.map((x) => x.value).join(";")
                }
            ];

            await this.apolloClient.query<IValidateDynamicTextResponse, any>({
                fetchPolicy: "cache-first",
                query: validateDynamicTextQuery,
                variables: {
                    id,
                    context: this.advancedEmailWizardContext.DynamicTextContext,
                    dictionaryToValidate,
                    participantID,
                },
            }).then((response: ApolloQueryResult<IValidateDynamicTextResponse>) => {
                resolve(response.data.communication.validateDynamicText);
            }).catch((error: ApolloError) => {
                reject(error);
            });
        });
    }

    private async validateBodyAndSubject(id: number, values: IWizardExecuteAllModel) {
        return new Promise<IEmailPreview>(async (resolve, reject) => {
            const email = values as SendEmailExecuteModel<ISendEmailDataModel | IParticipantEmailDataModel>;

            let participantID: number | undefined;

            if (this.advancedEmailWizardContext.WizardType === WizardTypeEnum.Participant) {
                const participantEmail = values as SendEmailExecuteModel<IParticipantEmailDataModel>;
                participantID = participantEmail.formDataModel.ParticipantEntryIDs[0];
            }

            await this.apolloClient.query<IEmailPreviewHtml, any>({
                fetchPolicy: "cache-first",
                query: previewEmailQuery,
                variables: {
                    id,
                    context: this.advancedEmailWizardContext.DynamicTextContext,
                    emailDetails: {
                        body: email.formDataModel.EmailBody,
                        subject: email.formDataModel.EmailSubject,
                    },
                    participantID,
                },
            }).then((response: ApolloQueryResult<IEmailPreviewHtml>) => {
                resolve(response.data.communication.previewEmailHtml);
            }).catch((error: ApolloError) => {
                reject(error);
            });
        });
    }
}
